import { FC } from "react";
import { Box, Typography } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { groupNotificationsByDate } from "@/modules/notification-center/utility";
import NotificationsWrapper from "@/modules/notification-center/components/notification/notifications-wrapper";
import { useNotificationCenterContext } from "@/modules/notification-center/context/notification-center.context";
import NotificationCardRenderer from "@/modules/notification-center/components/notification/notification-card-renderer";
import NotificationSkeletonList from "@/modules/notification-center/components/notification/notification-skeletons/notifications-skeleton-list";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

const GroupedNotifications: FC<{ maxToRender?: number }> = ({
  maxToRender,
}) => {
  const { notifications, isNotificationsLoading, params } =
    useNotificationCenterContext();

  const { grouped, sortedKeys } = groupNotificationsByDate(
    maxToRender && maxToRender > 0
      ? notifications.slice(0, maxToRender)
      : notifications
  );

  const noDataYet = isNotificationsLoading && notifications.length === 0;
  const isEmpty = !isNotificationsLoading && notifications.length === 0;

  const { read, systemTemplateId, createdAtMax, createdAtMin } = params || {};
  const isFiltering =
    read === false || !!systemTemplateId || !!createdAtMax || !!createdAtMin;

  if (noDataYet) {
    return <NotificationSkeletonList />;
  }

  if (isEmpty) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          width: "100%",
        }}
      >
        <Box
          sx={{
            gap: 2,
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            padding: "10px 20px",
          }}
        >
          <NotificationsIcon sx={{ width: 24, height: 24 }} />
          <Typography
            sx={{
              ...nawyTheme.nawy.typography.english.variants.headline.xs,
              color: nawyTheme.nawy.colors.semantic.text.primary,
            }}
          >
            {isFiltering
              ? "No notifications matching your filters."
              : "No notifications yet."}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {sortedKeys.map((dateKey) => (
        <NotificationsWrapper key={dateKey} date={dateKey}>
          {grouped[dateKey].map((notification) => (
            <NotificationCardRenderer
              key={notification.id}
              notification={notification}
            />
          ))}
        </NotificationsWrapper>
      ))}
      {isNotificationsLoading && (
        <Box sx={{ padding: "16px" }}>
          <NotificationSkeletonList count={1} />
        </Box>
      )}
    </>
  );
};

export default GroupedNotifications;
